const validators = {
  emailValidator,
  i18nPhoneNumberValidator,
  lengthValidator,
  matchOtherFieldValidator,
  passwordValidator,
  postalCodeValidatorFactory,
  urlValidator,
};

export { validators };

const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const lowerCaseRegex = new RegExp('[a-z]+');
const upperCaseRegex = new RegExp('[A-Z]+');
const numberRegex = new RegExp('[0-9]+');
const postalCodeRegexes = {
  US: /(^\d{5}$)|(^\d{5}-\d{4}$)/,
  CA: /^(?:[ABCEGHJ-NPRSTVXY]\d[A-Z][ -]?\d[A-Z]\d)$/i,
};
const countryKeys = {
  Canada: 'CA',
  CA: 'CA',
  'United States': 'US',
  US: 'US',
  'United States of America': 'US',
};

const urlRegex = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
  'i'
); // fragment locator

function emailValidator(value) {
  if (!emailRegex.test(String(value).toLowerCase())) {
    return 'Invalid email';
  }
  return null;
}

/**
 * Intended to be used with InternationalPhoneNumber Input, which returns a libphonenumber number object
 * @param {InternationalPhoneNumber} value - value from libphonenumber
 * @returns
 */
function i18nPhoneNumberValidator(phoneNum) {
  if (!phoneNum || !phoneNum.isValid) {
    return null;
  }
  if (!phoneNum.isValid()) {
    return 'Invalid phone number';
  }
  return null;
}

//TODO: rename to factory
function matchOtherFieldValidator(otherFieldName, otherFieldDisplayName) {
  return (value, fields) => {
    if (value !== fields[otherFieldName]) {
      return `Must match ${otherFieldDisplayName}`;
    }
    return null;
  };
}

//TODO: rename to factory
function lengthValidator({ min = null, max = null }) {
  return (value) => {
    const text = String(value);
    if (!min && !max) {
      // eslint-disable-next-line no-console
      console.error('length validator requires either min or max');
    }
    if (min) {
      if (text.length < min) {
        return `Must be at least ${min} characters`;
      }
    }
    if (max) {
      if (text.length > max) {
        return `Must not be longer than ${min} characters`;
      }
    }
    return null;
  };
}

export const PASSWORD_RULES = [
  {
    rule: (val) => (val && val.length >= 8) || false,
    descriptionKey: 'password.requirement.rule.min-characters',
  },
  {
    rule: (val) => (val && lowerCaseRegex.test(val)) || false,
    descriptionKey: 'password.requirement.rule.min-lowercase',
  },
  {
    rule: (val) => (val && upperCaseRegex.test(val)) || false,
    descriptionKey: 'password.requirement.rule.min-uppercase',
  },
  {
    rule: (val) => (val && numberRegex.test(val)) || false,
    descriptionKey: 'password.requirement.rule.min-number',
  },
];

function passwordValidator(value, fields, intl) {
  return PASSWORD_RULES.map((rule) => {
    return {
      descriptionKey: rule.descriptionKey,
      passed: rule.rule(value),
    };
  })
    .filter((validationResult) => !validationResult.passed)
    .map((validationResult) => intl.formatMessage({ id: validationResult.descriptionKey }))
    .join(', ');
}

/**
 * Create a postalCodeValidator based on the country input from the same form
 * @param {string} country - either the name of a field on the same ChowbusForm.Form, which containins the country code,
 *                           or the country code itself
 * @returns {function} postalCodeValidator
 */
function postalCodeValidatorFactory(country) {
  /**
   * The postal code validator itself
   * @param {string} code - postal code
   * @param {object} fields - Fields from the ChowbusForm context
   * @returns {string|null} string means invalid, null means valid
   */
  function postalCodeValidator(code, fields) {
    const cntry = fields && fields[country] ? fields[country] : country;
    const countryCode = countryKeys[cntry];
    const regex = postalCodeRegexes[countryCode];
    // If country is not known, validate against all countries
    const regexes = regex ? [regex] : Object.values(postalCodeRegexes);
    if (!regexes.some((re) => re.test(code))) {
      return 'Invalid postal code';
    }
    return null;
  }
  return postalCodeValidator;
}

function urlValidator(str) {
  if (!str) {
    return 'URL cannot be blank';
  }
  if (!urlRegex.test(str)) {
    return 'Invalid URL';
  }
  return null;
}
